'use client';

import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Chip, ChipGroup } from '@tranquility-ai/theme-components/chip';
import { DataList, DataListRow } from '@tranquility-ai/theme-components/data-list';
import { Pagination } from '@tranquility-ai/theme-components/pagination';
import { StatCard } from '@tranquility-ai/theme-components/stat-card';
import { Modal } from '@tranquility-ai/theme-components/modal';
import { Tag } from '@tranquility-ai/theme-components/tag';
import { StatusPill } from '@tranquility-ai/theme-components/status-pill';
import { Icon, Inline, Stack, Surface, Text } from '@tranquility-ai/theme-components/primitives';
import '@tranquility-ai/theme-components/modal.css';
import '@tranquility-ai/theme-components/pagination.css';
import { CsamReportNavBar, type CsamReportTab } from '@/components/csam-report-nav-bar';
import { CsamSectionHeader } from '@/components/csam-section-header';
import { useAppFormatters } from '@/i18n/useAppFormatters';
import { useTimePilotT, type TranslateFn } from '@/i18n/useTimePilotT';
import {
  ShieldWarningIcon,
  EyeSlashIcon,
  CaretLeftIcon,
  CaretRightIcon,
  LockKeyIcon,
  DownloadSimpleIcon,
  PaperPlaneTiltIcon,
  CheckCircleIcon,
  HourglassIcon,
  WarningCircleIcon,
  ArrowSquareOutIcon,
  FilmReelIcon,
  SpinnerGapIcon,
  BoundingBoxIcon,
} from '@phosphor-icons/react';
import { useContentModerationSummary } from '@/hooks/useContentModerationSummary';
import { useContentModerationFindings } from '@/hooks/useContentModerationFindings';
import { useContentModerationFindingDetail } from '@/hooks/useContentModerationFindingDetail';
import { useUpdateFindingStatus } from '@/hooks/useUpdateFindingStatus';
import { useFindingFileInfoPreview, safeViewUrlFromFileInfo, originalUrlFromFileInfo } from '@/hooks/useFindingOriginalPreview';
import { isAllCasesSelection } from '@/shared/case-summary';
import { encodeEvidenceLockerStyleProxyUriSegment, getCaseDisplayName } from '@/shared/functions';
import { useCsamAccess } from '@/lib/csam-access';
import {
  countUserFlagged,
  filterUserFlagged,
  isCaseFullyReviewed,
  isUserFlagged,
  paginateItems,
  provenanceLabel,
  resolvedClientListCount,
} from '@/lib/csam-user-flagged';
import { ContentModerationPreviewSwitcher } from './ui/content-moderation-preview-switcher';
import CsamAuditLog from './csam-audit-log';
import './csam-report.css';
import type {
  ContentModerationFindingSummary,
  DetectionResult,
  DetectionType,
  FindingStatusValue,
  NormalizedBoundingBox,
  NudeNetDetection,
} from '@/shared/cors-provenance-api';

// This report only ever deals with CSAM findings; the provenance content
// moderation endpoints require the detection type on every call.
const DETECTION_TYPE: DetectionType = 'CSAM';
/** Rows drawn on one list page. The request below is much larger. */
const FINDINGS_PAGE_SIZE = 8;
/** Findings pulled per request. The list no longer reads S3, so this is cheap. */
const FINDINGS_FETCH_LIMIT = 200;

// ---------------------------------------------------------------------------
// UI status (client-side display labels, separate from the backend enum)
// ---------------------------------------------------------------------------

type UiStatus = 'Confirmed' | 'Needs Review' | 'Reviewed';

const REVIEW_ACTIONS: { value: Exclude<UiStatus, 'Needs Review'>; labelKey: string }[] = [
  { value: 'Reviewed', labelKey: 'csam.action.notCsam' },
  { value: 'Confirmed', labelKey: 'csam.action.confirm' },
];

function toUiStatus(backendStatus: string): UiStatus {
  if (backendStatus === 'CONFIRMED') return 'Confirmed';
  if (backendStatus === 'REVIEWED') return 'Reviewed';
  return 'Needs Review'; // FLAGGED or unknown
}

function findingUiStatus(finding: ContentModerationFindingSummary): UiStatus {
  return toUiStatus(finding.findingStatus);
}

function toBackendStatus(uiStatus: UiStatus): FindingStatusValue {
  if (uiStatus === 'Confirmed') return 'CONFIRMED';
  if (uiStatus === 'Reviewed') return 'REVIEWED';
  return 'FLAGGED';
}

function formatCount(count: number | null): string {
  return count == null ? '—' : String(count);
}

// ---------------------------------------------------------------------------
// Helpers — derive display info from a raw finding
// ---------------------------------------------------------------------------

const VIDEO_EXTENSIONS = new Set(['mp4', 'avi', 'mov', 'mkv', 'wmv', 'flv', 'webm', 'm4v']);

function detectMediaType(url: string): 'IMAGE' | 'VIDEO' {
  const ext = url.split('.').pop()?.toLowerCase() ?? '';
  return VIDEO_EXTENSIONS.has(ext) ? 'VIDEO' : 'IMAGE';
}

function extractFilename(url: string): string {
  return url.split('/').pop() || url;
}

// The content-moderation API returns the obfuscated asset as an ``s3://`` URI,
// not a directly fetchable URL. Route it through the BFF S3 proxy (the same
// convention the Evidence Locker and media viewers use), which resolves the
// presigned URL and streams the bytes server-side so the presigned URL is never
// exposed to the browser.
function obfuscatedPreviewSrc(obfuscatedUrl: string): string {
  return `/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(obfuscatedUrl)}`;
}

function formatFlaggedAt(
  iso: string,
  formatDateTime: (value: string, options?: Intl.DateTimeFormatOptions) => string,
): string {
  try {
    return formatDateTime(iso, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Confidence helpers
// ---------------------------------------------------------------------------

type ConfidenceLevel = 'High' | 'Medium' | 'Low';

/** Flatten NudeNet boxes into a {label, confidence} list. Other detectors have no boxes. */
function flatDetectedLabels(results: DetectionResult[] | undefined): Array<{ label: string; confidence: number }> {
  return (results ?? []).flatMap(
    (r: DetectionResult) => (r.detections ?? []).map((d: NudeNetDetection) => ({ label: d.class, confidence: d.score }))
  );
}

/**
 * Normalize a detection ``box`` to fractional ``left/top/width/height`` in ``[0, 1]``.
 * Accepts the current object shape or a legacy ``[x, y, w, h]`` array. Pixel
 * arrays (any value ``> 1``) need natural image size to convert; return null
 * until that is known.
 */
function normalizeDetectionBox(
  box: NudeNetDetection['box'],
  naturalWidth?: number,
  naturalHeight?: number,
): NormalizedBoundingBox | null {
  if (Array.isArray(box)) {
    if (box.length < 4) return null;
    const [x, y, w, h] = box;
    if (![x, y, w, h].every((n) => typeof n === 'number' && Number.isFinite(n))) {
      return null;
    }
    const looksFractional = x <= 1 && y <= 1 && w <= 1 && h <= 1;
    if (looksFractional) {
      return { left: x, top: y, width: w, height: h };
    }
    if (!naturalWidth || !naturalHeight || naturalWidth <= 0 || naturalHeight <= 0) {
      return null;
    }
    return {
      left: x / naturalWidth,
      top: y / naturalHeight,
      width: w / naturalWidth,
      height: h / naturalHeight,
    };
  }
  if (
    box &&
    typeof box === 'object' &&
    typeof box.left === 'number' &&
    typeof box.top === 'number' &&
    typeof box.width === 'number' &&
    typeof box.height === 'number'
  ) {
    return box;
  }
  return null;
}

/** Detections that carry a usable bounding box (for overlay rendering). */
function detectionsWithBoxes(results: DetectionResult[] | undefined): NudeNetDetection[] {
  return (results ?? []).flatMap((r) =>
    (r.detections ?? []).filter((d) => normalizeDetectionBox(d.box) !== null || Array.isArray(d.box)),
  );
}

/**
 * Content rect of an ``object-fit: contain`` image inside its element box —
 * letterboxing offsets so fractional boxes land on the painted pixels.
 */
function objectContainContentRect(
  elW: number,
  elH: number,
  naturalW: number,
  naturalH: number,
): { x: number; y: number; width: number; height: number } {
  if (elW <= 0 || elH <= 0 || naturalW <= 0 || naturalH <= 0) {
    return { x: 0, y: 0, width: elW, height: elH };
  }
  const elRatio = elW / elH;
  const imgRatio = naturalW / naturalH;
  if (imgRatio > elRatio) {
    const height = elW / imgRatio;
    return { x: 0, y: (elH - height) / 2, width: elW, height };
  }
  const width = elH * imgRatio;
  return { x: (elW - width) / 2, y: 0, width, height: elH };
}

function confidenceLevel(score: number): ConfidenceLevel {
  const pct = score * 100;
  if (pct >= 80) return 'High';
  if (pct >= 50) return 'Medium';
  return 'Low';
}

function formatConfidence(score: number): string {
  return `${Math.round(score * 100)}%`;
}

function confidenceLevelLabel(level: ConfidenceLevel, t: TranslateFn): string {
  if (level === 'High') return t('csam.confidence.high');
  if (level === 'Medium') return t('csam.confidence.medium');
  return t('csam.confidence.low');
}

const CONFIDENCE_STYLES: Record<ConfidenceLevel, string> = {
  High: 'bg-red-500/20 text-red-400 border border-red-500/30',
  Medium: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
  Low: 'bg-gray-500/20 text-gray-400 border border-gray-500/30',
};

function ProvenanceTag({ finding }: { finding: ContentModerationFindingSummary }) {
  const t = useTimePilotT();
  return (
    <StatusPill
      size="sm"
      inverse
      color={isUserFlagged(finding) ? 'brand' : 'neutral'}
    >
      {provenanceLabel(finding, t)}
    </StatusPill>
  );
}

function StatusIcon({ status }: { status: UiStatus }) {
  if (status === 'Confirmed') return <WarningCircleIcon size={13} weight="fill" />;
  if (status === 'Needs Review') return <HourglassIcon size={13} weight="fill" />;
  return <CheckCircleIcon size={13} weight="fill" />;
}

function statusTagColor(status: UiStatus) {
  if (status === 'Confirmed') return 'danger' as const;
  if (status === 'Reviewed') return 'success' as const;
  return 'warning' as const;
}

/** Verdict copy. Internal status values stay Confirmed / Reviewed. */
function statusLabelKey(status: UiStatus): string {
  if (status === 'Confirmed') return 'csam.status.confirmed';
  if (status === 'Reviewed') return 'csam.status.cleared';
  return 'csam.status.underReview';
}

function StatusBadge({
  status,
  label,
}: {
  status: UiStatus;
  label?: string;
}) {
  const t = useTimePilotT();
  return (
    <StatusPill
      size="sm"
      inverse
      color={statusTagColor(status)}
      leadingIcon={<StatusIcon status={status} />}
    >
      {label ?? t(statusLabelKey(status))}
    </StatusPill>
  );
}

function ReviewActionButtons({
  status,
  onAction,
  disabled,
  values,
}: {
  status: UiStatus;
  onAction: (next: UiStatus) => void;
  disabled?: boolean;
  values?: Array<Exclude<UiStatus, 'Needs Review'>>;
}) {
  const t = useTimePilotT();
  const actions = values
    ? REVIEW_ACTIONS.filter((action) => values.includes(action.value))
    : REVIEW_ACTIONS;

  return (
    <>
      {actions.map(({ value, labelKey }) => {
        const solid = value === 'Confirmed';
        return (
          <Button
            key={value}
            size="sm"
            style={solid ? 'solid' : 'text'}
            color={solid ? 'accent' : 'light'}
            background="inverse"
            onClick={() => onAction(value)}
            disabled={disabled || value === status}
            data-testid={value === 'Confirmed' ? 'csam-confirm-action' : 'csam-not-csam-action'}
          >
            {t(labelKey)}
          </Button>
        );
      })}
    </>
  );
}

function DescriptionToggle({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  const t = useTimePilotT();
  return (
    <Button
      type="button"
      size="sm"
      style="text"
      color="accent"
      background="inverse"
      onClick={onToggle}
      aria-expanded={expanded}
      className="inline-flex h-[1.625em] min-h-0 items-baseline px-0 py-0 align-baseline text-sm font-medium leading-relaxed [--tp-button-border-width:0px] [--tp-button-content-height:1em] [--tp-button-pad-x:0px] [--tp-button-pad-y:0px]"
    >
      {expanded ? t('csam.showLess') : t('csam.showMore')}
    </Button>
  );
}

/**
 * LLM image description with a fixed collapsed height. Long text is clipped;
 * Show more / Show less sits inline at the end of the last line. Short /
 * empty text skips the toggle.
 */
function CollapsibleDescription({
  text,
  resetKey,
  panelClassName = 'bg-[#0d1a2b]',
}: {
  text: string | null | undefined;
  /** Change this when navigating findings so the section re-collapses. */
  resetKey: string;
  /** Solid panel background so the inline toggle can cover clipped text. */
  panelClassName?: string;
}) {
  const t = useTimePilotT();
  const body = text?.trim() || t('csam.noDescription');
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const contentRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    setExpanded(false);
  }, [resetKey]);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    // Only measure while collapsed — once expanded, clientHeight grows to fit
    // and would incorrectly clear the overflow flag (hiding "Show less").
    if (expanded) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [body, expanded, resetKey]);

  const toggle = () => setExpanded((value) => !value);

  return (
    <div>
      <div className="text-white/30 text-[10px] font-semibold tracking-widest uppercase mb-1.5">
        {t('eventModal.description')}
      </div>
      <div className="relative">
        <p
          ref={contentRef}
          className={`text-white/80 text-sm leading-relaxed${expanded ? '' : ' line-clamp-3'}`}
        >
          {body}
          {expanded && overflows ? (
            <>
              {' '}
              <DescriptionToggle expanded onToggle={toggle} />
            </>
          ) : null}
        </p>
        {!expanded && overflows ? (
          <span
            className={`absolute right-0 bottom-0 inline-flex h-[1.625em] items-baseline pl-8 ${panelClassName}`}
          >
            <span className="text-white/80 text-sm leading-relaxed" aria-hidden>
              …{' '}
            </span>
            <DescriptionToggle expanded={false} onToggle={toggle} />
          </span>
        ) : null}
      </div>
    </div>
  );
}

function ThumbnailPlaceholder({ type }: { type: 'IMAGE' | 'VIDEO' }) {
  return (
    <div className="flex h-full w-full items-center justify-center rounded-md border border-white/10 bg-[#111d2e]">
      {type === 'VIDEO' ? (
        <FilmReelIcon size={22} className="text-white/30" />
      ) : (
        <EyeSlashIcon size={22} className="text-white/30" />
      )}
    </div>
  );
}

// Row thumbnail. Renders the obfuscated (blurred) preview when one is available,
// falling back to the icon placeholder when there's no URL or the image fails to
// load.
function FindingThumbnail({
  finding,
  type,
}: {
  finding: ContentModerationFindingSummary;
  type: 'IMAGE' | 'VIDEO';
}) {
  const [errored, setErrored] = useState(false);

  if (!finding.obfuscatedUrl || errored) {
    return <ThumbnailPlaceholder type={type} />;
  }

  return (
    <div className="h-full w-full overflow-hidden rounded-md border border-white/10 bg-[#111d2e]">
      <img
        src={obfuscatedPreviewSrc(finding.obfuscatedUrl)}
        alt=""
        loading="lazy"
        className="w-full h-full object-cover"
        onError={() => setErrored(true)}
      />
    </div>
  );
}

// Large detail/preview pane. Renders whichever preview ``src`` is supplied
// (obfuscated or original), a loading spinner while an original is being
// fetched, or a "restricted" placeholder when nothing is available.
// `className` controls the box size and layout; the component supplies the
// background, border, and content.
//
// When ``showBoundingBoxes`` is on, NudeNet detection boxes are drawn as
// percentages of the painted ``object-contain`` image area (both views).
function FindingPreview({
  src,
  alt,
  loading = false,
  className,
  detections = [],
  showBoundingBoxes = false,
}: {
  src: string | null;
  alt: string;
  loading?: boolean;
  className: string;
  detections?: NudeNetDetection[];
  showBoundingBoxes?: boolean;
}) {
  const t = useTimePilotT();
  const [errored, setErrored] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const [layout, setLayout] = useState<{
    naturalW: number;
    naturalH: number;
    elW: number;
    elH: number;
  } | null>(null);

  // Reset the error state whenever the source changes (e.g. toggling between
  // obfuscated and original) so a prior failure doesn't mask a valid image.
  useEffect(() => {
    setErrored(false);
    setLayout(null);
  }, [src]);

  const measure = useCallback(() => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) return;
    setLayout({
      naturalW: img.naturalWidth,
      naturalH: img.naturalHeight,
      elW: img.clientWidth,
      elH: img.clientHeight,
    });
  }, []);

  useEffect(() => {
    if (!showBoundingBoxes || !src || errored) return;
    measure();
    const img = imgRef.current;
    if (!img) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(img);
    return () => ro.disconnect();
  }, [showBoundingBoxes, src, errored, measure]);

  if (loading) {
    return (
      <div
        className={`${className} bg-[#111d2e] border border-white/10 flex flex-col items-center justify-center gap-2`}
        role="status"
        aria-busy="true"
      >
        <SpinnerGapIcon size={18} className="animate-spin text-muted-foreground" aria-hidden />
        <span className="text-[14px] text-muted-foreground">{t('csam.loading')}</span>
      </div>
    );
  }

  if (!src || errored) {
    return (
      <div
        className={`${className} bg-[#111d2e] border border-white/10 flex flex-col items-center justify-center gap-2.5`}
      >
        <LockKeyIcon size={34} className="text-white/20" />
        <span className="text-white/40 text-xs font-semibold tracking-widest uppercase">
          {t('csam.restricted')}
        </span>
        <span className="text-white/25 text-[10px]">{t('csam.noPreview')}</span>
      </div>
    );
  }

  const contentRect =
    layout && showBoundingBoxes
      ? objectContainContentRect(layout.elW, layout.elH, layout.naturalW, layout.naturalH)
      : null;

  return (
    <div
      className={`${className} bg-[#111d2e] border border-white/10 flex items-center justify-center relative ${
        showBoundingBoxes ? 'overflow-visible z-10' : 'overflow-hidden'
      }`}
    >
      {/* Clip the image to rounded corners without clipping hover labels. */}
      <div className="absolute inset-0 overflow-hidden rounded-[inherit] flex items-center justify-center">
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className="w-full h-full object-contain"
          onError={() => setErrored(true)}
          onLoad={measure}
        />
      </div>
      {contentRect && detections.length > 0 && (
        <div
          className="absolute overflow-visible"
          style={{
            left: contentRect.x,
            top: contentRect.y,
            width: contentRect.width,
            height: contentRect.height,
          }}
          aria-hidden
        >
          {detections.map((d, i) => {
            const box = normalizeDetectionBox(
              d.box,
              layout?.naturalW,
              layout?.naturalH,
            );
            if (!box) return null;
            return (
              <div
                key={`${d.class}-${i}`}
                className="group absolute z-10 border border-amber-400/90 shadow-[0_0_0_1px_rgba(0,0,0,0.45)] hover:z-20 overflow-visible"
                style={{
                  left: `${box.left * 100}%`,
                  top: `${box.top * 100}%`,
                  width: `${box.width * 100}%`,
                  height: `${box.height * 100}%`,
                }}
              >
                <span className="pointer-events-none absolute bottom-full left-0 mb-0.5 whitespace-nowrap rounded bg-amber-400 px-1 py-px text-[9px] font-semibold leading-tight text-black opacity-0 transition-opacity group-hover:opacity-100">
                  {formatDetectionLabel(d.class)} {Math.round(d.score * 100)}%
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Compact on/off control for detection bounding boxes (obfuscated and original). */
function BoundingBoxesToggle({
  enabled,
  onToggle,
  disabled = false,
}: {
  enabled: boolean;
  onToggle: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      size="sm"
      style={enabled ? 'solid' : 'outline'}
      color="light"
      background="inverse"
      disabled={disabled}
      aria-pressed={enabled}
      onClick={() => onToggle(!enabled)}
      className={`mt-2 inline-flex w-full items-center justify-center gap-1.5 text-xs font-medium disabled:opacity-40 ${
        enabled
          ? 'border-amber-400/40 bg-amber-400/15 text-amber-300'
          : 'border-white/15 bg-white/5 text-white/55 hover:border-white/25 hover:text-white/75'
      }`}
    >
      <BoundingBoxIcon size={14} weight={enabled ? 'fill' : 'regular'} />
      {enabled ? 'Hide detection results' : 'Show detection results'}
    </Button>
  );
}

function SkeletonRow() {
  return (
    <DataListRow
      leading={<span className="tp-csam-report__skeleton tp-csam-report__skeleton--thumb" />}
      title={<span className="tp-csam-report__skeleton tp-csam-report__skeleton--title" />}
      description={<span className="tp-csam-report__skeleton tp-csam-report__skeleton--path" />}
      meta={<span className="tp-csam-report__skeleton tp-csam-report__skeleton--meta" />}
    />
  );
}

function MetaRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-white/30 text-[10px] font-semibold tracking-widest uppercase mb-0.5">
        {label}
      </div>
      <div
        className={`text-white/80 text-sm break-all ${mono ? 'font-mono text-xs' : ''}`}
        title={value}
      >
        {value || '—'}
      </div>
    </div>
  );
}

function formatDetectionLabel(label: string): string {
  return label.replace(/_/g, ' ');
}

function uniqueDetectionLabels(
  labels: Array<{ label: string }>,
  fallback?: string,
): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const { label } of labels) {
    const text = label.trim();
    if (!text || seen.has(text)) continue;
    seen.add(text);
    unique.push(text);
  }
  if (unique.length === 0 && fallback?.trim()) return [fallback.trim()];
  return unique;
}

function DetectionLabelTags({
  title,
  labels,
  fallback,
}: {
  title: string;
  labels: Array<{ label: string }>;
  fallback?: string;
}) {
  const items = uniqueDetectionLabels(labels, fallback);
  return (
    <div>
      <div className="text-white/30 text-[10px] font-semibold tracking-widest uppercase mb-1.5">
        {title}
      </div>
      {items.length === 0 ? (
        <div className="text-white/80 text-sm">—</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((label) => (
            <Tag
              key={label}
              size="sm"
              inverse
              color="neutral"
              label={formatDetectionLabel(label)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Finding detail modal
// ---------------------------------------------------------------------------

interface FindingDetailModalProps {
  caseId: string | null;
  finding: ContentModerationFindingSummary;
  uiStatus: UiStatus;
  isPending: boolean;
  onAction: (uuid: string, status: UiStatus) => void;
  onClose: () => void;
}

function FindingDetailModal({
  caseId,
  finding,
  uiStatus,
  isPending,
  onAction,
  onClose,
}: FindingDetailModalProps) {
  const t = useTimePilotT();
  const { formatDateTime } = useAppFormatters();
  const mediaType = detectMediaType(finding.filePath);
  const filename = extractFilename(finding.filePath);
  const detailQuery = useContentModerationFindingDetail(caseId, finding);
  const detail = detailQuery.data;
  const labels = flatDetectedLabels(detail?.detectionResults);
  const descriptionText = detailQuery.isLoading
    ? t('csam.detail.loading')
    : detailQuery.isError
      ? t('csam.detail.error')
      : detail?.imageDescription;

  const topScore = typeof detail?.confidence === 'number' ? detail.confidence : null;
  const topLevel = topScore !== null ? confidenceLevel(topScore) : null;

  // Only csam-reviewers can toggle to the original image; everyone else sees the
  // obfuscated preview with no control.
  const { canRevealOriginal, canVerifyFindings } = useCsamAccess();
  const [showObfuscatedPreview, setShowObfuscatedPreview] = useState(true);
  // Non-reviewers can never leave the obfuscated view: pin to `true` regardless of
  // the raw `showObfuscatedPreview` state so stale/false state (or cached
  // ``originalQuery`` data from a prior reveal) can't render the original when
  // ``canRevealOriginal`` is off or the toggle is hidden. Every preview decision
  // (fetch gating, render URL, alt text) must read this, not the raw state.
  const effectiveShowObfuscated = canRevealOriginal ? showObfuscatedPreview : true;
  const [showBoundingBoxes, setShowBoundingBoxes] = useState(false);
  const boxedDetections = detectionsWithBoxes(detail?.detectionResults);

  // Reset to the safe obfuscated view whenever a different finding is opened, so
  // a prior "Original" toggle never carries over to newly viewed content.
  // Detection overlays start hidden too — a prior "show" must not leak.
  useEffect(() => {
    setShowObfuscatedPreview(true);
    setShowBoundingBoxes(false);
  }, [finding.fileMd5]);

  // Default (safe) preview: prefer the finding's direct obfuscated asset, but
  // when it's absent resolve from file-info the same way the rest of the app
  // does — a still-moderated file exposes only its CM-safe ``previewUrl``, while
  // a cleared/reviewed file (whose obfuscated transcode has been dropped) shows
  // the real image instead of a "restricted" placeholder (see
  // ``safeViewUrlFromFileInfo``).
  const needObfuscatedFromFileInfo = effectiveShowObfuscated && !finding.obfuscatedUrl;
  const obfuscatedQuery = useFindingFileInfoPreview(finding.filePath, {
    bypassCm: false,
    enabled: needObfuscatedFromFileInfo,
  });
  const obfuscatedFromFileInfo = safeViewUrlFromFileInfo(obfuscatedQuery.data);

  // The original is sensitive content — only fetch it (via the audited
  // ``bypass_cm`` file-info path) once a reviewer has actually toggled to it.
  // Look up by the obfuscated asset's real ``s3://`` URL (not ``filePath`` /
  // searchable_path): ``toProvenanceLookupPath`` would otherwise prepend
  // ``data/bulk/`` and 404 for processed / subdocument images. With
  // ``bypass_cm=true``, provenance returns the finding's original ``previewUrl``.
  const wantsOriginal = !effectiveShowObfuscated && !!finding.obfuscatedUrl;
  const originalQuery = useFindingFileInfoPreview(finding.obfuscatedUrl, {
    bypassCm: true,
    enabled: wantsOriginal,
  });
  const originalUrl = originalUrlFromFileInfo(originalQuery.data);

  const obfuscatedSrc = finding.obfuscatedUrl
    ? obfuscatedPreviewSrc(finding.obfuscatedUrl)
    : obfuscatedFromFileInfo
      ? obfuscatedPreviewSrc(obfuscatedFromFileInfo)
      : null;
  const originalSrc = originalUrl ? obfuscatedPreviewSrc(originalUrl) : null;

  const previewSrc = effectiveShowObfuscated ? obfuscatedSrc : originalSrc;
  const previewLoading =
    (wantsOriginal && originalQuery.isPending) ||
    (needObfuscatedFromFileInfo && obfuscatedQuery.isPending);
  const previewAlt = effectiveShowObfuscated ? t('csam.obfuscatedPreview') : t('csam.originalImage');

  return (
    <Modal
      isOpen
      onClose={onClose}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={filename}
      size="xl"
      titleActions={<StatusBadge status={uiStatus} />}
      headerActions={
        canRevealOriginal ? (
          <ContentModerationPreviewSwitcher
            showObfuscatedPreview={showObfuscatedPreview}
            onToggle={setShowObfuscatedPreview}
          />
        ) : undefined
      }
      bodyClassName="pr-4"
      footer={
        <Inline justify="between" align="center" wrap>
          <Button
            size="sm"
            style="text"
            color="light"
            background="inverse"
            onClick={onClose}
          >
            {t('eventModal.close')}
          </Button>
          <Inline align="center" gap="sm">
            {canVerifyFindings ? (
              <>
                <ReviewActionButtons
                  status={uiStatus}
                  onAction={(next) => onAction(finding.fileMd5, next)}
                  disabled={isPending}
                />
                {isPending ? (
                  <Icon icon={SpinnerGapIcon} size="sm" className="animate-spin" />
                ) : null}
              </>
            ) : null}
          </Inline>
        </Inline>
      }
    >
      <Stack gap="lg">
        <CollapsibleDescription
          text={descriptionText}
          resetKey={finding.fileMd5}
          panelClassName="bg-[var(--tp-modal-bg,#0f1d32)]"
        />
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="flex-shrink-0 w-full sm:w-[260px]">
            <FindingPreview
              src={previewSrc}
              alt={previewAlt}
              loading={previewLoading}
              className="w-full h-[200px] sm:h-[280px] rounded-lg"
              detections={boxedDetections}
              showBoundingBoxes={showBoundingBoxes}
            />
            {boxedDetections.length > 0 && (
              <BoundingBoxesToggle
                enabled={showBoundingBoxes}
                onToggle={setShowBoundingBoxes}
                disabled={previewLoading || !previewSrc}
              />
            )}
          </div>
          <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
            <MetaRow label={t('csam.field.sourcePath')} value={finding.filePath} mono />
            <MetaRow label={t('csam.field.timestamp')} value={formatFlaggedAt(finding.flaggedAt, formatDateTime)} />
            <MetaRow label={t('csam.field.mediaType')} value={mediaType} />
            {detailQuery.isLoading || detailQuery.isError ? (
              <MetaRow
                label={t('csam.field.confidence')}
                value={detailQuery.isError ? t('csam.detail.error') : t('csam.detail.loading')}
              />
            ) : topScore !== null && topLevel !== null ? (
              <div>
                <div className="text-white/30 text-[10px] font-semibold tracking-widest uppercase mb-0.5">
                  {t('csam.field.confidence')}
                </div>
                <span className={`inline-flex justify-center min-w-[116px] px-2 py-0.5 rounded-full text-xs font-medium ${CONFIDENCE_STYLES[topLevel]}`}>
                  {confidenceLevelLabel(topLevel, t)} ({formatConfidence(topScore)})
                </span>
              </div>
            ) : (
              <MetaRow label={t('csam.field.confidence')} value={t('csam.confidence.unknown')} />
            )}
            <MetaRow label={t('csam.field.md5')} value={finding.fileMd5} mono />
            <MetaRow label={t('csam.field.flaggedBy')} value={provenanceLabel(finding, t)} />
            <div className="sm:col-span-2">
              {detailQuery.isLoading || detailQuery.isError ? (
                <MetaRow
                  label={t('csam.field.labels')}
                  value={detailQuery.isError ? t('csam.detail.error') : t('csam.detail.loading')}
                />
              ) : (
                <DetectionLabelTags
                  title={t('csam.field.labels')}
                  labels={labels}
                  fallback={finding.detectionType}
                />
              )}
            </div>
            {finding.reviewedBy && (
              <MetaRow
                label={t('csam.field.reviewedBy')}
                value={
                  finding.reviewedAt
                    ? `${finding.reviewedBy} · ${formatFlaggedAt(finding.reviewedAt, formatDateTime)}`
                    : finding.reviewedBy
                }
              />
            )}
          </div>
        </div>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Review mode — single-finding-at-a-time review workflow
// ---------------------------------------------------------------------------

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white/50 font-mono text-[10px]">
      {children}
    </kbd>
  );
}

interface ReviewModeProps {
  caseId: string | null;
  findings: ContentModerationFindingSummary[];
  resolveStatus: (finding: ContentModerationFindingSummary) => UiStatus;
  pendingUuid: string | null;
  reviewedCount: number;
  totalCount: number;
  onAction: (uuid: string, status: UiStatus) => void;
  onExit: () => void;
}

function ReviewMode({
  caseId,
  findings,
  resolveStatus,
  pendingUuid,
  reviewedCount,
  totalCount,
  onAction,
  onExit,
}: ReviewModeProps) {
  const t = useTimePilotT();
  const { formatDateTime } = useAppFormatters();
  const [index, setIndex] = useState(() => {
    const firstPending = findings.findIndex((finding) => resolveStatus(finding) === 'Needs Review');
    return firstPending >= 0 ? firstPending : 0;
  });

  // Only CSAM Verifiers can toggle to the original image; everyone else sees the
  // obfuscated preview with no control.
  const { canRevealOriginal, canVerifyFindings } = useCsamAccess();
  const [showObfuscatedPreview, setShowObfuscatedPreview] = useState(true);
  // Non-reviewers can never leave the obfuscated view: pin to `true` regardless of
  // the raw `showObfuscatedPreview` state so stale/false state (or cached
  // ``originalQuery`` data from a prior reveal) can't render the original when
  // ``canRevealOriginal`` is off or the toggle is hidden. Every preview decision
  // (fetch gating, render URL, alt text) must read this, not the raw state.
  const effectiveShowObfuscated = canRevealOriginal ? showObfuscatedPreview : true;
  const [showBoundingBoxes, setShowBoundingBoxes] = useState(false);

  // Reset to the safe obfuscated view whenever a different finding is shown, so a
  // prior "Original" toggle never carries over to newly viewed content.
  // Detection overlays start hidden too — a prior "show" must not leak.
  useEffect(() => {
    setShowObfuscatedPreview(true);
    setShowBoundingBoxes(false);
  }, [index]);

  // Freeze the full finding set for the session so prev/next and "N of M"
  // match Review Progress (total flagged), not just the leftover queue.
  // Verifying still updates status via resolveStatus; the component remounts
  // when review mode is re-entered.
  const [queue] = useState(() => findings);

  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setIndex((i) => Math.min(queue.length - 1, i + 1)),
    [queue.length],
  );

  const verify = useCallback(
    (nextStatus: UiStatus) => {
      const f = queue[index];
      if (!f) return;
      const wasPending = resolveStatus(f) === 'Needs Review';
      onAction(f.fileMd5, nextStatus);
      // Changing an existing designation stays on this finding. First-time
      // review still auto-advances, and verifying the last item exits.
      if (!wasPending) return;
      if (index >= queue.length - 1) {
        onExit();
        return;
      }
      setIndex((i) => i + 1);
    },
    [queue, index, onAction, onExit, resolveStatus],
  );

  // Keyboard shortcuts: ← prev, → next, 1 confirm, 2 not-csam, Esc exit.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (canVerifyFindings && e.key === '1') {
        e.preventDefault();
        if (resolveStatus(queue[index]) !== 'Confirmed') verify('Confirmed');
      } else if (canVerifyFindings && e.key === '2') {
        e.preventDefault();
        if (resolveStatus(queue[index]) !== 'Reviewed') verify('Reviewed');
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onExit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext, verify, onExit, canVerifyFindings, resolveStatus, queue, index]);

  const current = queue[index];

  // Default (safe) preview: prefer the finding's direct obfuscated asset, but
  // when it's absent resolve from file-info the same way the rest of the app
  // does — a still-moderated file exposes only its CM-safe ``previewUrl``, while
  // a cleared/reviewed file (whose obfuscated transcode has been dropped) shows
  // the real image instead of a "restricted" placeholder (see
  // ``safeViewUrlFromFileInfo``).
  const needObfuscatedFromFileInfo =
    effectiveShowObfuscated && !!current && !current.obfuscatedUrl;
  const obfuscatedQuery = useFindingFileInfoPreview(current?.filePath, {
    bypassCm: false,
    enabled: needObfuscatedFromFileInfo,
  });
  const obfuscatedFromFileInfo = safeViewUrlFromFileInfo(obfuscatedQuery.data);

  // The original is sensitive content — only fetch it (via the audited
  // ``bypass_cm`` file-info path) once a reviewer has toggled to it.
  // Look up by the obfuscated asset's real ``s3://`` URL (not ``filePath`` /
  // searchable_path): ``toProvenanceLookupPath`` would otherwise prepend
  // ``data/bulk/`` and 404 for processed / subdocument images. With
  // ``bypass_cm=true``, provenance returns the finding's original ``previewUrl``.
  const wantsOriginal =
    !effectiveShowObfuscated && !!current?.obfuscatedUrl;
  const originalQuery = useFindingFileInfoPreview(current?.obfuscatedUrl, {
    bypassCm: true,
    enabled: wantsOriginal,
  });
  const originalUrl = originalUrlFromFileInfo(originalQuery.data);
  const detailQuery = useContentModerationFindingDetail(caseId, current);
  // Warm the next finding so stepping forward does not wait on S3.
  useContentModerationFindingDetail(caseId, queue[index + 1]);

  if (queue.length === 0 || !current) {
    return (
      <Surface tone="inverse" radius="md" padding="lg">
        <Text variant="bodySmall" className="text-center">
          {t('csam.noFindings')}
        </Text>
      </Surface>
    );
  }

  const status = resolveStatus(current);
  const filename = extractFilename(current.filePath);
  const mediaType = detectMediaType(current.filePath);
  const detail = detailQuery.data;
  const currentLabels = flatDetectedLabels(detail?.detectionResults);
  const boxedDetections = detectionsWithBoxes(detail?.detectionResults);
  const score = typeof detail?.confidence === 'number' ? detail.confidence : null;
  const level = score !== null ? confidenceLevel(score) : null;
  const descriptionText = detailQuery.isLoading
    ? t('csam.detail.loading')
    : detailQuery.isError
      ? t('csam.detail.error')
      : detail?.imageDescription;
  const isPending = pendingUuid === current.fileMd5;
  const progressPct = totalCount > 0 ? Math.round((reviewedCount / totalCount) * 100) : 0;

  const obfuscatedSrc = current.obfuscatedUrl
    ? obfuscatedPreviewSrc(current.obfuscatedUrl)
    : obfuscatedFromFileInfo
      ? obfuscatedPreviewSrc(obfuscatedFromFileInfo)
      : null;
  const originalSrc = originalUrl ? obfuscatedPreviewSrc(originalUrl) : null;
  const previewSrc = effectiveShowObfuscated ? obfuscatedSrc : originalSrc;
  const previewLoading =
    (wantsOriginal && originalQuery.isPending) ||
    (needObfuscatedFromFileInfo && obfuscatedQuery.isPending);
  const previewAlt = effectiveShowObfuscated ? t('csam.obfuscatedPreview') : t('csam.originalImage');

  return (
    <div className="flex flex-col gap-4">
      {/* Progress */}
      <Surface tone="inverse" radius="md" padding="md">
        <div className="flex flex-row items-center justify-between mb-2">
          <span className="text-white/60 text-xs font-semibold tracking-widest uppercase">
            {t('csam.reviewProgress')}
          </span>
          <span className="text-white/40 text-xs">
            {t('csam.reviewedCount', { reviewed: reviewedCount, total: totalCount })}
          </span>
        </div>
        <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </Surface>

      {/* Finding card */}
      <Surface tone="inverse" radius="md" className="overflow-hidden">
        <CsamSectionHeader
          title={filename}
          titleAddon={<StatusBadge status={status} />}
          actions={
            canRevealOriginal ? (
              <ContentModerationPreviewSwitcher
                showObfuscatedPreview={showObfuscatedPreview}
                onToggle={setShowObfuscatedPreview}
              />
            ) : undefined
          }
        />

        {/* Description — LLM-generated summary so a reviewer can assess the
            content before revealing the original image. */}
        <div className="px-6 pt-6">
          <CollapsibleDescription
            text={descriptionText}
            resetKey={current.fileMd5}
            panelClassName="bg-[var(--color-ink-075,#1a263e)]"
          />
        </div>

        {/* Card body */}
        <div className="flex flex-col sm:flex-row gap-6 p-6">
          <div className="flex-shrink-0 w-full sm:w-[260px]">
            <FindingPreview
              src={previewSrc}
              alt={previewAlt}
              loading={previewLoading}
              className="w-full h-[200px] sm:h-[240px] rounded-lg"
              detections={boxedDetections}
              showBoundingBoxes={showBoundingBoxes}
            />
            {boxedDetections.length > 0 && (
              <BoundingBoxesToggle
                enabled={showBoundingBoxes}
                onToggle={setShowBoundingBoxes}
                disabled={previewLoading || !previewSrc}
              />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <div className="sm:col-span-2">
                <MetaRow label={t('csam.field.sourcePath')} value={current.filePath} />
              </div>
              <MetaRow label={t('csam.field.mediaType')} value={mediaType} />
              {detailQuery.isLoading || detailQuery.isError ? (
                <MetaRow
                  label={t('csam.field.confidence')}
                  value={detailQuery.isError ? t('csam.detail.error') : t('csam.detail.loading')}
                />
              ) : score !== null && level !== null ? (
                <div>
                  <div className="text-white/30 text-[10px] font-semibold tracking-widest uppercase mb-0.5">
                    {t('csam.field.confidence')}
                  </div>
                  <span
                    className={`inline-flex justify-center min-w-[116px] px-2 py-0.5 rounded-full text-xs font-medium ${CONFIDENCE_STYLES[level]}`}
                  >
                    {confidenceLevelLabel(level, t)} ({formatConfidence(score)})
                  </span>
                </div>
              ) : (
                <MetaRow label={t('csam.field.confidence')} value={t('csam.confidence.unknown')} />
              )}
              <MetaRow label={t('csam.field.detected')} value={formatFlaggedAt(current.flaggedAt, formatDateTime)} />
              <MetaRow label={t('csam.field.flaggedBy')} value={provenanceLabel(current, t)} />
              <MetaRow label={t('csam.field.md5')} value={current.fileMd5} mono />
              <div className="sm:col-span-2">
                {detailQuery.isLoading || detailQuery.isError ? (
                  <MetaRow
                    label={t('csam.field.systemLabels')}
                    value={detailQuery.isError ? t('csam.detail.error') : t('csam.detail.loading')}
                  />
                ) : (
                  <DetectionLabelTags
                    title={t('csam.field.systemLabels')}
                    labels={currentLabels}
                    fallback={current.detectionType}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Verification footer */}
        <div className="px-6 py-4 border-t border-white/10">
          <div className="flex flex-row items-center justify-between gap-4 flex-wrap">
            <div className="flex flex-row items-center gap-3 text-white/30 text-[11px]">
              <Inline align="center" gap="xs">
                <Button
                  iconOnly
                  size="sm"
                  style="text"
                  color="light"
                  background="inverse"
                  aria-label={t('csam.field.previousFinding')}
                  onClick={goPrev}
                  disabled={index === 0}
                >
                  <CaretLeftIcon />
                </Button>
                <span className="tabular-nums text-white/60">
                  {index + 1} of {queue.length}
                </span>
                <Button
                  iconOnly
                  size="sm"
                  style="text"
                  color="light"
                  background="inverse"
                  aria-label={t('csam.nextFinding')}
                  onClick={goNext}
                  disabled={index === queue.length - 1}
                >
                  <CaretRightIcon />
                </Button>
              </Inline>
              {canVerifyFindings && (
                <>
                  <span className="flex items-center gap-1"><Kbd>1</Kbd> {t('csam.action.confirm')}</span>
                  <span className="flex items-center gap-1"><Kbd>2</Kbd> {t('csam.action.notCsam')}</span>
                </>
              )}
              <span className="flex items-center gap-1"><Kbd>{t('csam.esc')}</Kbd> {t('csam.exit')}</span>
            </div>
            {canVerifyFindings && (
            <div className="flex flex-row items-center gap-2">
              <ReviewActionButtons
                status={status}
                onAction={verify}
                disabled={isPending}
                values={['Confirmed', 'Reviewed']}
              />
              {isPending && <SpinnerGapIcon size={14} className="text-white/40 animate-spin ml-1" />}
            </div>
            )}
          </div>
        </div>
      </Surface>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface CsamReportProps {
  caseId?: string;
  reviewer?: string;
  embeddedInAppShell?: boolean;
}

const CsamReport: React.FC<CsamReportProps> = ({
  caseId,
  reviewer,
  embeddedInAppShell = true,
}) => {
  const t = useTimePilotT();
  const { formatDateTime } = useAppFormatters();
  const [activeTab, setActiveTab] = useState<CsamReportTab>('list');
  const [selectedFinding, setSelectedFinding] = useState<ContentModerationFindingSummary | null>(null);
  // The detail modal unmounts while All Cases is selected. Drop the finding
  // in the same render as the case change so picking another case cannot
  // reopen a finding that belongs to the previous one.
  const [findingCaseId, setFindingCaseId] = useState(caseId);
  if (findingCaseId !== caseId) {
    setFindingCaseId(caseId);
    setSelectedFinding(null);
  }
  const { canVerifyFindings, canOpenAuditLog } = useCsamAccess();

  useEffect(() => {
    if (activeTab === 'review' && !canVerifyFindings) {
      setActiveTab('list');
    } else if (activeTab === 'audit' && !canOpenAuditLog) {
      setActiveTab('list');
    }
  }, [activeTab, canVerifyFindings, canOpenAuditLog]);

  // null = "All" (no server-side status filter applied).
  const [statusFilter, setStatusFilter] = useState<FindingStatusValue | null>(null);
  const [userFlaggedOnly, setUserFlaggedOnly] = useState(false);

  // 0-based index into the 8-row pages sliced from the flattened findings.
  const [listPageIndex, setListPageIndex] = useState(0);
  // Bumped when the list query identity changes so an in-flight cursor jump
  // cannot apply its target index after a filter reset has already moved to page 0.
  const listPageJumpGenRef = useRef(0);

  // Optimistic overrides keyed by md5 — applied immediately on click,
  // reverted if the mutation errors out.
  const [statusOverrides, setStatusOverrides] = useState<Record<string, UiStatus>>({});
  /** Why the last review decision did not stick. Cleared on the next attempt. */
  const [statusErrorMessage, setStatusErrorMessage] = useState<string | null>(null);

  const updateMutation = useUpdateFindingStatus(DETECTION_TYPE);

  // All Cases (`*` / "All Cases") is not a case. Skip summary and findings
  // fetches until the user picks one. `refetch()` ignores `enabled`, so the
  // review-mode effects below are gated on this too.
  const hasSelectedCase = Boolean(caseId?.trim()) && !isAllCasesSelection(caseId);
  const queryCaseId = hasSelectedCase ? caseId : null;

  const { data: summaryData } = useContentModerationSummary(
    queryCaseId,
    DETECTION_TYPE,
  );

  // Needs Review, Confirmed, and Cleared are one status each, so the list
  // query uses the case index prefix. User flagged is not a status: it reads
  // CONFIRMED findings and keeps the ones a person created. Review mode still
  // pages the whole case.
  const userFlaggedListActive = activeTab === 'list' && userFlaggedOnly;
  const effectiveStatus: FindingStatusValue | undefined =
    activeTab === 'review' ? undefined : userFlaggedOnly ? 'CONFIRMED' : statusFilter ?? undefined;

  const {
    data: findingsData,
    isLoading: findingsQueryLoading,
    isCaseResolving,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchFindings,
    isError: findingsError,
    isFetchNextPageError,
  } = useContentModerationFindings(queryCaseId, {
    detectionType: DETECTION_TYPE,
    status: effectiveStatus,
    limit: FINDINGS_FETCH_LIMIT,
    enabled: hasSelectedCase,
  });

  // The findings query stays disabled until the case's folder segment resolves,
  // and a disabled query reports ``isLoading === false`` with no data. Treating
  // that as a settled empty list skipped the skeleton, rendered "no flagged
  // sources", and computed "fully reviewed" for a case full of findings.
  const findingsLoading = findingsQueryLoading || isCaseResolving;

  const findings: ContentModerationFindingSummary[] =
    findingsData?.pages.flatMap((p) => p.items) ?? [];

  const userFlaggedFindings = filterUserFlagged(findings, true);
  const listForPaging = userFlaggedListActive ? userFlaggedFindings : findings;
  const currentPageFindings = paginateItems(listForPaging, listPageIndex, FINDINGS_PAGE_SIZE);

  // Reset page index in the same event as the filter change (not a useEffect) so
  // the first render after a filter switch already uses page 0. An effect-based
  // reset leaves one frame where the old index is applied to the new query's
  // cached pages, which can flash the empty-filter state when that query has
  // fewer pages.
  const handleStatusFilterChange = useCallback((key: FindingStatusValue | null) => {
    listPageJumpGenRef.current += 1;
    setStatusFilter(key);
    setListPageIndex(0);
  }, []);

  // Keep the page index in range if a refetch shrinks the loaded page set.
  useEffect(() => {
    const length = userFlaggedListActive ? userFlaggedFindings.length : findings.length;
    const maxIndex = Math.max(0, Math.ceil(length / FINDINGS_PAGE_SIZE) - 1);
    // A jump may sit past the rows loaded so far while another page is still due.
    if (!userFlaggedListActive && hasNextPage && listPageIndex > maxIndex) return;
    if (listPageIndex > maxIndex) setListPageIndex(maxIndex);
  }, [listPageIndex, userFlaggedListActive, userFlaggedFindings.length, findings.length, hasNextPage]);

  // Always pull fresh findings when entering review mode (the "All" list filter
  // shares this query key, so a tab switch alone won't trigger a refetch).
  // Then drain remaining pages so the review queue isn't truncated to page 1.
  useEffect(() => {
    if (!hasSelectedCase) return;
    if (activeTab !== 'review') return;
    refetchFindings();
  }, [activeTab, refetchFindings, hasSelectedCase]);

  // Review mode needs every finding. User flagged needs every CONFIRMED
  // finding so the client filter is complete. Status chips stop at one page
  // until the user moves past the rows already loaded. Stop after a next-page
  // failure: TanStack Query keeps hasNextPage true and clears
  // isFetchingNextPage, which would otherwise retrigger this effect forever.
  useEffect(() => {
    if (!hasSelectedCase) return;
    const shouldDrain = activeTab === 'review' || userFlaggedListActive;
    if (!shouldDrain) return;
    if (findingsError || isFetchNextPageError) return;
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [
    hasSelectedCase,
    activeTab,
    userFlaggedListActive,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    findingsError,
    isFetchNextPageError,
  ]);

  const handleListPageChange = useCallback(
    async (targetPage: number) => {
      const jumpGen = listPageJumpGenRef.current;
      const isStaleJump = () => listPageJumpGenRef.current !== jumpGen;
      const targetIndex = targetPage - 1;
      if (userFlaggedListActive) {
        if (!isStaleJump()) setListPageIndex(Math.max(0, targetIndex));
        return;
      }
      const itemsNeeded = targetPage * FINDINGS_PAGE_SIZE;
      let flatCount = findings.length;
      let canFetchMore = Boolean(hasNextPage);
      while (flatCount < itemsNeeded && canFetchMore) {
        if (isStaleJump()) return;
        const previousCount = flatCount;
        const result = await fetchNextPage();
        if (isStaleJump()) return;
        const nextCount =
          result.data?.pages.reduce((count, page) => count + page.items.length, 0) ?? previousCount;
        if (nextCount <= previousCount) break;
        flatCount = nextCount;
        canFetchMore = Boolean(result.hasNextPage);
      }
      if (isStaleJump()) return;
      const maxIndex = Math.max(0, Math.ceil(flatCount / FINDINGS_PAGE_SIZE) - 1);
      setListPageIndex(Math.min(Math.max(0, targetIndex), maxIndex));
    },
    [userFlaggedListActive, findings.length, hasNextPage, fetchNextPage],
  );

  const totalFlagged = summaryData
    ? summaryData.flaggedCount + summaryData.reviewedCount + summaryData.confirmedCount
    : null;
  const pagingComplete = (!hasNextPage || isFetchNextPageError) && !findingsLoading;
  const needsReviewCount = summaryData?.flaggedCount ?? null;
  const confirmedCount = summaryData?.confirmedCount ?? null;
  const reviewedCount = summaryData?.reviewedCount ?? null;

  // Count for the currently-active status filter (null = All).
  const filteredCount = userFlaggedOnly
    ? resolvedClientListCount(null, userFlaggedFindings.length, pagingComplete)
    : statusFilter === 'CONFIRMED'
      ? confirmedCount
      : statusFilter === 'FLAGGED'
        ? needsReviewCount
        : statusFilter === 'REVIEWED'
          ? reviewedCount
          : totalFlagged;

  const listTotalPages = userFlaggedListActive
    ? Math.max(
        1,
        Math.ceil(
          Math.max(filteredCount ?? 0, userFlaggedFindings.length) / FINDINGS_PAGE_SIZE,
        ),
      )
    : filteredCount != null
      ? Math.max(1, Math.ceil(filteredCount / FINDINGS_PAGE_SIZE))
      : Math.max(
          listPageIndex + 1,
          Math.ceil(findings.length / FINDINGS_PAGE_SIZE),
          hasNextPage ? listPageIndex + 2 : listPageIndex + 1,
        );
  // A failed next-page fetch leaves hasNextPage true. Do not keep the
  // skeleton up — show whatever pages already loaded plus the error.
  const userFlaggedListLoading =
    userFlaggedListActive &&
    !isFetchNextPageError &&
    (findingsLoading || hasNextPage || isFetchingNextPage);

  // Resolves a finding's display status, honoring optimistic overrides.
  const resolveStatus = (f: ContentModerationFindingSummary): UiStatus =>
    statusOverrides[f.fileMd5] ?? findingUiStatus(f);

  // Which finding (if any) currently has an in-flight status mutation.
  const pendingUuid = updateMutation.isPending
    ? updateMutation.variables?.fileMd5 ?? null
    : null;

  // Review progress follows the drained queue so missing attention counts
  // cannot shrink "N / M reviewed" or hide leftover user flags.
  const reviewedProgress = findings.filter((finding) => resolveStatus(finding) !== 'Needs Review')
    .length;
  const reviewTotal = findings.length;

  const handleAction = (uuid: string, nextUiStatus: UiStatus) => {
    const previousStatus = statusOverrides[uuid];
    setStatusErrorMessage(null);
    setStatusOverrides((prev) => ({ ...prev, [uuid]: nextUiStatus }));
    updateMutation.mutate(
      { fileMd5: uuid, status: toBackendStatus(nextUiStatus), caseId: caseId ?? '' },
      {
        onError: (error) => {
          setStatusErrorMessage(
            error instanceof Error && error.message
              ? error.message
              : t('csam.reviewSaveFailed'),
          );
          setStatusOverrides((prev) => {
            const next = { ...prev };
            if (previousStatus !== undefined) {
              next[uuid] = previousStatus;
            } else {
              delete next[uuid];
            }
            return next;
          });
        },
      },
    );
  };

  const caseName = getCaseDisplayName(caseId) || '—';
  const isFullyReviewed = isCaseFullyReviewed({
    needsReviewCount,
    findingsAreUnfiltered: effectiveStatus === undefined,
    pagingComplete: !hasNextPage && !findingsLoading && !isFetchNextPageError,
    awaitingReviewCount: findings.filter((finding) => finding.findingStatus === 'FLAGGED').length,
  });

  return (
    <div className="tp-csam-report">
      <CsamReportNavBar
        embeddedInAppShell={embeddedInAppShell}
        caseName={caseName}
        reviewer={reviewer}
        activeTab={activeTab}
        onActiveTabChange={setActiveTab}
        isFullyReviewed={isFullyReviewed}
        showReviewTab={canVerifyFindings}
        showAuditTab={canOpenAuditLog}
        hasSelectedCase={hasSelectedCase}
      />
      {/* Finding detail modal */}
      {hasSelectedCase && selectedFinding && (
        <FindingDetailModal
          caseId={queryCaseId ?? null}
          finding={selectedFinding}
          uiStatus={
            statusOverrides[selectedFinding.fileMd5] ?? findingUiStatus(selectedFinding)
          }
          isPending={
            updateMutation.isPending &&
            updateMutation.variables?.fileMd5 === selectedFinding.fileMd5
          }
          onAction={handleAction}
          onClose={() => setSelectedFinding(null)}
        />
      )}

      {/* Main content */}
      <div className="tp-csam-report__body">
        {!hasSelectedCase ? (
          <div
            className="flex min-h-[50vh] items-center justify-center px-4 text-center"
            role="status"
          >
            <Text variant="bodySmall">{t('csam.selectCase')}</Text>
          </div>
        ) : (
        <>

        {activeTab !== 'audit' && (
          <div className="tp-csam-report__stats" aria-label={t('csam.stats.summary')}>
            <StatCard
              tone="inverse"
              label={t('csam.stats.totalFlagged')}
              value={formatCount(totalFlagged)}
              icon={<ShieldWarningIcon weight="fill" />}
            />
            <StatCard
              tone="inverse"
              label={t('csam.stats.confirmed')}
              value={formatCount(confirmedCount)}
              icon={<WarningCircleIcon weight="fill" />}
            />
            <StatCard
              tone="inverse"
              label={t('csam.stats.needsReview')}
              value={formatCount(needsReviewCount)}
              icon={<HourglassIcon weight="fill" />}
            />
            <StatCard
              tone="inverse"
              label={t('csam.stats.cleared')}
              value={formatCount(reviewedCount)}
              icon={<CheckCircleIcon weight="fill" />}
            />
          </div>
        )}

        {activeTab === 'audit' && canOpenAuditLog ? (
          <CsamAuditLog embedded />
        ) : activeTab === 'review' && canVerifyFindings ? (
          findingsError || isFetchNextPageError ? (
            <Surface tone="inverse" padding="lg" radius="md">
              <Text variant="bodySmall">{t('csam.loadError')}</Text>
            </Surface>
          ) : findingsLoading || hasNextPage || isFetchingNextPage ? (
            <Surface tone="inverse" padding="lg" radius="md">
              <Inline gap="sm" align="center" justify="center">
                <Icon icon={SpinnerGapIcon} size="sm" />
                <Text variant="bodySmall">{t('csam.review.loading')}</Text>
              </Inline>
            </Surface>
          ) : (
            <ReviewMode
              caseId={queryCaseId ?? null}
              findings={findings}
              resolveStatus={resolveStatus}
              pendingUuid={pendingUuid}
              reviewedCount={reviewedProgress}
              totalCount={reviewTotal}
              onAction={handleAction}
              onExit={() => setActiveTab('list')}
            />
          )
        ) : (
        <>
        <Stack gap="md">
        <ChipGroup>
          {[
            { key: null, labelKey: 'csam.filter.all', count: totalFlagged },
            { key: 'FLAGGED' as const, labelKey: 'csam.filter.needsReview', count: needsReviewCount },
            { key: 'CONFIRMED' as const, labelKey: 'csam.filter.confirmed', count: confirmedCount },
            { key: 'REVIEWED' as const, labelKey: 'csam.filter.cleared', count: reviewedCount },
          ].map(({ key, labelKey, count }) => (
            <Chip
              key={labelKey}
              type="filter"
              surface="inverse"
              size="sm"
              label={t(labelKey, { count: formatCount(count) })}
              selected={statusFilter === key && !userFlaggedOnly}
              onToggle={() => {
                setUserFlaggedOnly(false);
                handleStatusFilterChange(key);
              }}
            />
          ))}
          <Chip
            type="filter"
            surface="inverse"
            size="sm"
            label={t('csam.filter.userFlagged', {
              count: formatCount(
                userFlaggedOnly && pagingComplete ? countUserFlagged(findings) : null,
              ),
            })}
            selected={userFlaggedOnly}
            onToggle={() => {
              setUserFlaggedOnly((prev) => !prev);
              handleStatusFilterChange(null);
            }}
          />
        </ChipGroup>

        <Stack gap="sm">
          <Inline align="center" justify="between">
            <Text variant="label" weight="semibold">
              {t('csam.sources.title')}
            </Text>
            {!findingsLoading && !userFlaggedListLoading && currentPageFindings.length > 0 && (
              <Text variant="bodyTiny">
                {(() => {
                  const start = listPageIndex * FINDINGS_PAGE_SIZE + 1;
                  const end = listPageIndex * FINDINGS_PAGE_SIZE + currentPageFindings.length;
                  const total = filteredCount;
                  return total != null
                    ? t('csam.sources.range', { start, end, total })
                    : t('csam.sources.rangePartial', { start, end });
                })()}
              </Text>
            )}
            {!findingsLoading && !userFlaggedListLoading && currentPageFindings.length === 0 && filteredCount != null && (
              <Text variant="bodyTiny">{t('csam.sources.itemCount', { count: filteredCount })}</Text>
            )}
          </Inline>

          {(findingsError || isFetchNextPageError) && (
            <Text variant="bodySmall">{t('csam.loadError')}</Text>
          )}

          {/* A failed status change reverts the chip; without this the revert is
              silent and reads as the review decision simply not sticking. */}
          {statusErrorMessage && (
            <Text variant="bodySmall">{statusErrorMessage}</Text>
          )}

          {(findingsLoading || userFlaggedListLoading) && !findingsError && (
            <DataList tone="inverse" aria-label={t('csam.sources.loading')}>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </DataList>
          )}

          {!findingsLoading && !userFlaggedListLoading && !findingsError && (
            <DataList
              tone="inverse"
              aria-label={t('csam.sources.list')}
              empty={
                userFlaggedOnly
                  ? t('csam.sources.emptyUser')
                  : statusFilter === null
                    ? t('csam.sources.empty')
                    : t('csam.sources.emptyFilter')
              }
            >
              {currentPageFindings.map((finding) => {
                const mediaType = detectMediaType(finding.filePath);
                const filename = extractFilename(finding.filePath);
                const date = formatFlaggedAt(finding.flaggedAt, formatDateTime);
                const uiStatus =
                  statusOverrides[finding.fileMd5] ?? findingUiStatus(finding);
                const isRowPending =
                  updateMutation.isPending &&
                  updateMutation.variables?.fileMd5 === finding.fileMd5;

                return (
                  <DataListRow
                    key={finding.fileMd5}
                    leading={
                      isRowPending ? (
                        <Icon icon={SpinnerGapIcon} size="md" />
                      ) : (
                        <FindingThumbnail finding={finding} type={mediaType} />
                      )
                    }
                    title={filename}
                    description={finding.filePath}
                    titleAddon={<ProvenanceTag finding={finding} />}
                    meta={date}
                    status={
                      uiStatus === 'Needs Review' ? undefined : <StatusBadge status={uiStatus} />
                    }
                    actions={
                      canVerifyFindings && uiStatus === 'Needs Review' ? (
                        <ReviewActionButtons
                          status={uiStatus}
                          onAction={(next) => handleAction(finding.fileMd5, next)}
                          disabled={isRowPending}
                        />
                      ) : undefined
                    }
                    trailing={
                      <Button
                        iconOnly
                        size="sm"
                        style="text"
                        color="light"
                        background="inverse"
                        aria-label={`Open ${filename}`}
                      >
                        <CaretRightIcon />
                      </Button>
                    }
                    onClick={() => {
                      if (!isRowPending) setSelectedFinding(finding);
                    }}
                  />
                );
              })}
            </DataList>
          )}

          {!findingsLoading && !userFlaggedListLoading && !findingsError && (
            <Pagination
              variant="inverse"
              size="sm"
              page={listPageIndex + 1}
              totalPages={listTotalPages}
              onPageChange={(page) => void handleListPageChange(page)}
              disabled={isFetchingNextPage}
              previousLabel={t('media.previous')}
              nextLabel={t('media.next')}
              aria-label={t('sources.flaggedPages')}
            />
          )}
        </Stack>
        </Stack>
        </>
        )}
        </>
        )}
      </div>

      {/* Sticky bottom action bar */}
      {/* <div className="flex-shrink-0 border-t border-white/10 bg-[#09121E] py-3 flex flex-row justify-center">
        <div className="flex flex-row items-center gap-3 flex-wrap px-8 sm:px-12 lg:px-16 w-full max-w-[1400px]">
          <button className="flex flex-row items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors">
            <ArrowSquareOutIcon size={16} />
            <span>Report to NCMEC</span>
          </button>
          <button className="flex flex-row items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white/80 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
            <LockKeyIcon size={16} />
            <span>Evidence Locker</span>
          </button>
          <button className="flex flex-row items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white/80 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
            <DownloadSimpleIcon size={16} />
            <span>Export Report</span>
          </button>
          <button className="flex flex-row items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white/80 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
            <PaperPlaneTiltIcon size={16} />
            <span>Request Additional Review</span>
          </button>
        </div>
      </div> */}
    </div>
  );
};

export default CsamReport;
